/**
 * "Walk the finished-work step" (closed loop Phase 3, D5) — what a foreman walks ONCE, on his own phone, before a
 * Capex-awarded job's first daily report. Its "Ready for the field" checklist (Project Financials → the job → Ready
 * for the field) has a box a person ticks for exactly that: "the foreman walked the finished-work step once on his
 * phone". This tour is the thing to walk, so the tick means something.
 *
 * WHO. A foreman (tk:create), at a company with Project Financials on — the step exists only there
 * (`DailyReportEntry` draws `WorkCompletedStep` only while `capex` is on), so the tour asks for that switch.
 *
 * A PRACTICE RUN, like the daily-report tour it borrows its first two steps from: nothing is saved or submitted
 * (the default `practice`, see tourSchema.js) — he opens a blank report, puts hours on a job, opens the step and
 * reads what it asks. The last step takes him off the report screen, so the practice report goes with it.
 *
 * IN THE REGISTRY (`./index.js`, after `firstDailyReport`). Its fourth step points at `daily-report.work-completed`,
 * the finished-work step's header button, which renders only where Project Financials is on - so that step names
 * its own harness screen (`/timekeeping/report/new#project-financials`), the report screen with the switch on, and
 * the anti-rot guard (`__tests__/tourAnchors.contract.test.jsx`) checks the anchor there.
 */
import { defineTour, AUDIENCE, ADVANCE_ON } from '../tourSchema'

export default defineTour({
  id: 'walk-the-finished-work-step',
  title: 'Walk the finished-work step',
  summary: 'Once, on your phone, before the first report on a job priced in Project Financials: what “What did we finish today” asks and why.',
  audience: AUDIENCE.ONBOARDING,
  module: 'timekeeping',
  requiresSwitch: 'capex',
  permission: 'tk:create',

  steps: [
    {
      id: 'open-daily-reports',
      anchor: 'timekeeping.nav.daily-reports',
      route: '/timekeeping',
      module: 'timekeeping',
      title: 'Start where you always do',
      body: 'Everything here happens on the daily report you already fill in. Open Daily Reports.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'daily-report.new' },
    },
    {
      id: 'start-a-report',
      anchor: 'daily-report.new',
      route: '/timekeeping/reports',
      module: 'timekeeping',
      title: 'Open a blank one',
      body: 'This is a practice run: nothing you do on this report is saved or sent.',
      advanceOn: ADVANCE_ON.DO,
      until: { appears: 'daily-report.date' },
    },
    {
      id: 'hours-first',
      anchor: 'daily-report.hours-entry',
      route: '/timekeeping/report/new',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'Hours first, on the job',
      body: 'Put your crew’s hours on the job as you always do. The step below lists only the jobs that have hours on them — so a job with no hours has nothing to tick.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'what-did-we-finish',
      anchor: 'daily-report.work-completed',
      route: '/timekeeping/report/new',
      // The same screen with Project Financials ON - the step is drawn only there (a harness key, not a route).
      screen: '/timekeeping/report/new#project-financials',
      module: 'timekeeping',
      permission: 'tk:create',
      title: 'What did we finish today',
      body: 'Open it. It lists the job’s take-off lines — the work the job was priced on. Tick only what was FINISHED today: a weld welded, a run of pipe laid and tested. Half-done work waits for the day it is finished. If nothing was finished, say so — that is an answer too. Your hours are never held up by this step, and the office uses what you tick to price the next job.',
      advanceOn: ADVANCE_ON.NEXT,
    },
    {
      id: 'thats-it',
      anchor: 'timekeeping.nav.my-hours',
      route: '/timekeeping',
      module: 'timekeeping',
      title: 'That is the whole step',
      body: 'Every day you report this job: hours first, then tick what was finished. Tell the office you have walked it — they tick it on the job’s “Ready for the field” checklist.',
      advanceOn: ADVANCE_ON.NEXT,
    },
  ],
})
